import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowUpCircle, AlertTriangle } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useSession } from "@/hooks/useSession";
import { getMaxResources } from "@/utils/subscriptionLimits";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ResourceUsageProps {
  usedResources: number;
  maxResources: number;
  nextResetDate: string;
  onUpgradeClick: () => void;
}

export const ResourceUsage = ({
  usedResources,
  maxResources,
  nextResetDate,
  onUpgradeClick,
}: ResourceUsageProps) => {
  const { session } = useSession();
  const { data: profile, isLoading } = useProfile(session?.user?.id);
  
  const currentUsage = profile?.resources_used_this_month || 0;
  const usagePercentage = (currentUsage / maxResources) * 100;
  const isNearLimit = usagePercentage > 80;
  const isInactive = profile?.billing_status === 'inactive';

  if (isInactive) {
    return (
      <Alert variant="destructive" className="max-w-md">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Your account is inactive. Please update your billing information to continue using the service.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1 min-w-[200px] px-3 py-1">
      <div className="flex justify-between w-full items-center">
        <div className="text-xs text-gray-600">
          Resources Created: {currentUsage}/{maxResources}
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 text-xs gap-1 text-primary" 
          onClick={onUpgradeClick}
        >
          Upgrade <ArrowUpCircle className="h-3 w-3" />
        </Button>
      </div>
      <Progress 
        value={usagePercentage} 
        className={`h-2 w-full ${isNearLimit ? "bg-red-100" : "bg-green-100"}`}
      />
      <div className="text-xs text-gray-500">
        Resets on: {nextResetDate}
      </div>
      {isNearLimit && (
        <div className="text-xs text-red-600 mt-1">
          You're near your resource limit. Consider upgrading your plan.
        </div>
      )}
    </div>
  );
};